import { combineReducers } from '@reduxjs/toolkit';

import { resetOnLogoutReducer } from '@utils';

import {
  CLEAR_PEOPLE,
  DESELECT_PERSON,
  ModalMode,
  SELECT_MULTIPLE_PERSONS,
  SELECT_PERSON,
  SET_SELECTED_ACTIVITY,
  activityValuesTabs,
} from '../../common';
import { detailModes } from '../constants';
import {
  CLOSE_CAMPOUTS,
  CLOSE_CAMPOUTS_MODAL_RESTORE,
  OPEN_CAMPOUTS,
  OPEN_CAMPOUTS_CREATE,
  OPEN_CAMPOUTS_MODAL_RESTORE,
  OPEN_EDIT_CAMPOUT,
  RECORD_CAMPOUTS_ERROR,
  RECORD_CAMPOUTS_REQUEST,
  RECORD_CAMPOUTS_RESPONSE,
  SET_ACTIVE_TAB,
  SET_DETAIL_MODE,
  SET_SELECTED_CAMPOUTS,
} from './actions';

const visible = (state = false, { type }) => {
  switch (type) {
    case OPEN_CAMPOUTS_CREATE:
    case OPEN_CAMPOUTS:
    case OPEN_EDIT_CAMPOUT: {
      return true;
    }
    case CLOSE_CAMPOUTS: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const modalMode = (state = ModalMode.RECORD, { type }) => {
  switch (type) {
    case OPEN_CAMPOUTS: {
      return ModalMode.RECORD;
    }
    case OPEN_CAMPOUTS_CREATE: {
      return ModalMode.CREATE;
    }
    case OPEN_EDIT_CAMPOUT: {
      return ModalMode.EDIT;
    }
    default: {
      return state;
    }
  }
};

const allowRemovePersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
    case OPEN_CAMPOUTS_CREATE:
      return payload.allowRemovePersons;
    case OPEN_EDIT_CAMPOUT:
      return true;
    default:
      return state;
  }
};

const disableGetActivities = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
      return payload.disableGetActivities;
    case OPEN_CAMPOUTS_CREATE:
    case OPEN_EDIT_CAMPOUT:
      return false;
    default:
      return state;
  }
};

const disableFutureDays = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
      return payload.disableFutureDays;
    case OPEN_CAMPOUTS_CREATE:
    case OPEN_EDIT_CAMPOUT:
      return false;
    default:
      return state;
  }
};

const detailMode = (state = detailModes.SUGGEST, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
      return detailModes.SUGGEST;
    case OPEN_CAMPOUTS_CREATE: {
      const { dates: { start } = {} } = payload;
      if (start) {
        return detailModes.CREATE;
      }
      return detailModes.SUGGEST;
    }
    case OPEN_EDIT_CAMPOUT: {
      return detailModes.EDIT;
    }
    case SET_DETAIL_MODE: {
      return payload;
    }
    default: {
      return state;
    }
  }
};

const loading = (state = false, { type }) => {
  switch (type) {
    case RECORD_CAMPOUTS_REQUEST: {
      return true;
    }
    case RECORD_CAMPOUTS_RESPONSE:
    case RECORD_CAMPOUTS_ERROR: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const userIds = (state = [], { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS: {
      return payload.userIds || [];
    }
    case CLEAR_PEOPLE:
    case OPEN_CAMPOUTS_CREATE:
    case OPEN_EDIT_CAMPOUT: {
      return [];
    }
    case DESELECT_PERSON: {
      return state.filter(userId => userId !== payload);
    }
    case SELECT_PERSON:
      return [...new Set([...state, payload])];
    case SELECT_MULTIPLE_PERSONS:
      return [...new Set([...state, ...payload])];
    default: {
      return state;
    }
  }
};

const selectedCampout = (state = null, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
    case OPEN_CAMPOUTS_CREATE: {
      return null;
    }
    case SET_SELECTED_CAMPOUTS: {
      return payload;
    }
    case SET_DETAIL_MODE: {
      if (payload === detailModes.SUGGEST) {
        return null;
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

const dates = (state = {}, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
    case OPEN_EDIT_CAMPOUT: {
      return {};
    }
    case OPEN_CAMPOUTS_CREATE: {
      return payload.dates;
    }
    default: {
      return state;
    }
  }
};

const activeTab = (state = activityValuesTabs.BASIC, { type, payload }) => {
  switch (type) {
    case OPEN_CAMPOUTS:
    case OPEN_CAMPOUTS_CREATE: {
      return activityValuesTabs.BASIC;
    }
    case SET_ACTIVE_TAB: {
      return payload;
    }
    case SET_SELECTED_ACTIVITY: {
      const { areSameAdvancedValues = true } = payload || {};
      return areSameAdvancedValues
        ? activityValuesTabs.BASIC
        : activityValuesTabs.ADVANCED;
    }
    default: {
      return state;
    }
  }
};

const allowEditPersons = (state = false, { type, payload }) => {
  switch (type) {
    case OPEN_EDIT_CAMPOUT:
    case OPEN_CAMPOUTS_CREATE: {
      const { allowEditOrAddPersonsPersons = false } = payload || {};
      return allowEditOrAddPersonsPersons;
    }
    case OPEN_CAMPOUTS: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const isVisibleCampoutsModalRestore = (state = false, { type }) => {
  switch (type) {
    case OPEN_CAMPOUTS_MODAL_RESTORE: {
      return true;
    }
    case CLOSE_CAMPOUTS_MODAL_RESTORE: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const reducer = combineReducers({
  visible,
  loading,
  userIds,
  modalMode,
  detailMode,
  dates,
  selectedCampout,
  activeTab,
  allowEditPersons,
  disableGetActivities,
  disableFutureDays,
  allowRemovePersons,
  isVisibleCampoutsModalRestore,
});

export default resetOnLogoutReducer(reducer);
